import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { PaymentStatusEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { isUserPreferencesResolved } from '../../lib/store/app/helpers';
import { getCurrentAccount } from '../../lib/store/crm/helpers';
import { getCurrentBuyingPower, getOldestAdjustmentOfCurrentAccount } from '../../lib/store/selectors';
import { clearDemoAccountActivating } from '../../lib/util/account-helpers';
import { clearPlanPurchasePaymentCallback } from '../../lib/util/url-helpers';

import SuccessLogo from '../../assets/img/green_checkmark.png';
import { useIsMobile } from '../../hooks';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { BaseModal, MobileDrawer } from '../index';

import styles from './AccountReadyPopup.module.scss';

const STORAGE_KEY_PREFIX = 'accountReadyPopupShown_';

const AccountReadyPopup: FC<ReduxProps> = (props) => {
  const {
    currentAccount,
    firstAdjustment,
    buyingPow,
    hasSeenWelcomePopup,
    arePreferencesResolved,
  } = props;
  const { isMobile } = useIsMobile();

  const [isNewestAdjustmentCompleted, setIsNewestAdjustmentCompleted] = useState<
    boolean | 'initial'
  >('initial');
  const [isOpen, setIsOpen] = useState(false);

  useLayoutEffect(() => {
    if (firstAdjustment) {
      setIsNewestAdjustmentCompleted(
        firstAdjustment.payment_status === PaymentStatusEnum.Completed,
      );
    } else {
      setIsNewestAdjustmentCompleted(false);
    }
  }, [firstAdjustment, currentAccount]);

  useEffect(() => {
    if (isNewestAdjustmentCompleted === 'initial') return;

    if (!arePreferencesResolved || !hasSeenWelcomePopup) return;

    const isNotificationVisible = !isNewestAdjustmentCompleted && !!currentAccount;

    if (!isNotificationVisible || buyingPow <= 0) return;

    const storageKey = STORAGE_KEY_PREFIX + (currentAccount || 'default');
    if (!sessionStorage.getItem(storageKey)) {
      setIsOpen(true);
      sessionStorage.setItem(storageKey, '1');
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    isNewestAdjustmentCompleted,
    buyingPow,
    arePreferencesResolved,
    hasSeenWelcomePopup,
  ]);

  if (!isOpen) return <></>;

  const handleClose = () => {
    setIsOpen(false);
    clearDemoAccountActivating();
    clearPlanPurchasePaymentCallback();
  };

  const content = (
    <>
      <div className={styles.content}>
        <div className={styles.iconCircle}>
          <img src={SuccessLogo} alt="success" />
        </div>
        <div className={styles.title}>{lang.commonAccountIsAllSet()}</div>
        <div className={styles.text}>{lang.commonAccountIsAllSetDescription()}</div>
      </div>
      <div className={styles.buttonsWrapper}>
        <Button fullWidth variant={ButtonTypesEnum.Primary} onClick={handleClose} biggerFontSize>
          {lang.commonOkay()}
        </Button>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <MobileDrawer
        title=""
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        showCloseButton={false}
        showTitle={false}
      >
        {content}
      </MobileDrawer>
    );
  }

  return (
    <BaseModal
      className={styles.modal}
      isOpen={isOpen}
      toggle={() => setIsOpen(false)}
    >
      {content}
    </BaseModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentAccount: getCurrentAccount(state),
  firstAdjustment: getOldestAdjustmentOfCurrentAccount(state),
  buyingPow: getCurrentBuyingPower(state),
  hasSeenWelcomePopup: state.app.userPreferencesData?.hasSeenWelcomePopup,
  arePreferencesResolved: isUserPreferencesResolved(state.app),
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(AccountReadyPopup);
